import { useEffect, useMemo, useState } from "react";
import { useI18n } from "../context/I18nContext";

const W = 1200;
const H = 630;

function buildShareSvg({ questionText, yes, no, yesPct, noPct, total, labels }) {
  const leaf = `
    <g transform="translate(960 60)">
      <svg width="180" height="180" viewBox="2400 0 4800 4800" preserveAspectRatio="xMidYMid meet">
        <path fill="#E00000" d="m0 0h2400l99 99h4602l99-99h2400v4800h-2400l-99-99h-4602l-99 99H0z"/>
        <path fill="#F4F4F4" d="m2400 0h4800v4800h-4800zm2490 4430-45-863a95 95 0 0 1 111-98l859 151-116-320a65 65 0 0 1 20-73l941-762-212-99a65 65 0 0 1-34-79l186-572-542 115a65 65 0 0 1-73-38l-105-247-423 454a65 65 0 0 1-111-57l204-1052-327 189a65 65 0 0 1-91-27l-332-652-332 652a65 65 0 0 1-91 27l-327-189 204 1052a65 65 0 0 1-111 57l-423-454-105 247a65 65 0 0 1-73 38l-542-115 186 572a65 65 0 0 1-34 79l-212 99 941 762a65 65 0 0 1 20 73l-116 320 859-151a95 95 0 0 1 111 98l-45 863z"/>
      </svg>
    </g>
  `;

  // Naive word-wrap (up to 3 lines)
  const words = (questionText || "").split(" ");
  const lines = [];
  let line = "";
  for (const w of words) {
    if ((line + " " + w).trim().length > 42 && line) {
      lines.push(line.trim());
      line = w;
    } else {
      line = (line + " " + w).trim();
    }
  }
  if (line) lines.push(line.trim());
  const qLines = lines.slice(0, 3);

  const totalWidth = 980;
  const yesWidth = total === 0 ? 0 : (yesPct / 100) * totalWidth;
  const noWidth = total === 0 ? 0 : (noPct / 100) * totalWidth;

  // XML-safe escape for the SVG text content
  const esc = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet" style="width:100%;height:auto;display:block">
  <rect width="${W}" height="${H}" fill="#F4F4F4"/>
  <rect x="8" y="8" width="${W - 16}" height="${H - 16}" fill="none" stroke="#000000" stroke-width="8"/>
  <g transform="translate(60 70)">
    <text font-family="Georgia, serif" font-weight="900" font-size="44" fill="#000000" letter-spacing="-1">Canadian</text>
    <text x="0" y="50" font-family="Georgia, serif" font-style="italic" font-weight="900" font-size="40" fill="#000000" letter-spacing="-1">Opinions<tspan fill="#E00000">.</tspan></text>
  </g>
  ${leaf}
  <g transform="translate(60 200)">
    <rect width="320" height="38" fill="#E00000"/>
    <text x="160" y="26" text-anchor="middle" font-family="ui-monospace, monospace" font-weight="900" font-size="16" fill="#ffffff" letter-spacing="3">${esc(labels.live)}</text>
  </g>
  ${qLines.map((l, i) => `<text x="60" y="${290 + i * 50}" font-family="Georgia, serif" font-weight="800" font-size="42" fill="#000000" letter-spacing="-1">${esc('"' + l + '"')}</text>`).join("\n  ")}
  <g transform="translate(60 ${330 + qLines.length * 50})">
    <rect width="540" height="170" fill="#ffffff" stroke="#000000" stroke-width="4"/>
    <text x="20" y="34" font-family="ui-monospace, monospace" font-weight="900" font-size="16" fill="#000000" letter-spacing="3">${esc(labels.yesLabel)}</text>
    <text x="270" y="120" text-anchor="middle" font-family="Georgia, serif" font-weight="900" font-size="92" fill="#000000">${yes.toLocaleString()}</text>
    <g transform="translate(20 140)">
      <rect width="120" height="22" fill="#000000"/>
      <text x="60" y="16" text-anchor="middle" font-family="ui-monospace, monospace" font-weight="900" font-size="13" fill="#ffffff" letter-spacing="2">${esc(labels.yesChip)} · ${yesPct}%</text>
    </g>
    <g transform="translate(560 0)">
      <rect width="540" height="170" fill="#ffffff" stroke="#000000" stroke-width="4"/>
      <text x="520" y="34" text-anchor="end" font-family="ui-monospace, monospace" font-weight="900" font-size="16" fill="#000000" letter-spacing="3">${esc(labels.noLabel)}</text>
      <text x="270" y="120" text-anchor="middle" font-family="Georgia, serif" font-weight="900" font-size="92" fill="#E00000">${no.toLocaleString()}</text>
      <g transform="translate(400 140)">
        <rect width="120" height="22" fill="#E00000"/>
        <text x="60" y="16" text-anchor="middle" font-family="ui-monospace, monospace" font-weight="900" font-size="13" fill="#ffffff" letter-spacing="2">${esc(labels.noChip)} · ${noPct}%</text>
      </g>
    </g>
  </g>
  ${total > 0 ? `<g transform="translate(60 ${525 + qLines.length * 50})">
    <rect width="${totalWidth}" height="22" fill="#ffffff" stroke="#000000" stroke-width="3"/>
    <rect x="0" y="0" width="${yesWidth}" height="22" fill="#000000"/>
    <rect x="${yesWidth}" y="0" width="${noWidth}" height="22" fill="#E00000"/>
  </g>` : ""}
  <g transform="translate(60 ${H - 28})">
    <text font-family="ui-monospace, monospace" font-weight="900" font-size="14" fill="#000000" letter-spacing="3">${total.toLocaleString()} ${esc(labels.footer)}</text>
  </g>
</svg>`;
}

export default function ShareModal({ open, onClose, question, result }) {
  const { t, pick } = useI18n();
  const [downloading, setDownloading] = useState(false);

  const questionText = question ? pick(question, "text") : "";

  const labels = useMemo(() => ({
    live: t("share_card_live"),
    yesLabel: t("share_card_yes_label"),
    noLabel: t("share_card_no_label"),
    yesChip: t("share_card_yes_chip"),
    noChip: t("share_card_no_chip"),
    footer: (result?.total === 1) ? t("share_card_footer_singular") : t("share_card_footer_plural"),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [t, result?.total]);

  const svgString = useMemo(() => {
    if (!question || !result) return "";
    return buildShareSvg({
      questionText,
      yes: result.yes, no: result.no,
      yesPct: result.yes_pct, noPct: result.no_pct,
      total: result.total,
      labels,
    });
  }, [question, result, questionText, labels]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !question || !result) return null;

  const downloadPng = async () => {
    setDownloading(true);
    try {
      const sizedSvg = svgString.replace(
        '<svg xmlns="http://www.w3.org/2000/svg"',
        `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}"`
      );
      const img = new Image();
      const svgBlob = new Blob([sizedSvg], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(svgBlob);
      await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject; img.src = url; });
      const canvas = document.createElement("canvas");
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, W, H);
      URL.revokeObjectURL(url);
      const pngUrl = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = pngUrl;
      a.download = `canadian-opinions-${question.id}-tally.png`;
      a.click();
    } finally {
      setDownloading(false);
    }
  };

  const shareText = t("share_tweet_template")
    .replace("{n}", result.total.toLocaleString())
    .replace("{q}", questionText)
    .replace("{yes}", String(result.yes_pct))
    .replace("{no}", String(result.no_pct));
  const shareUrl = typeof window !== "undefined" ? window.location.origin : "";

  const openTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };
  const openFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };
  const copyText = async () => {
    try { await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`); } catch {}
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="share-modal">
      <div className="bg-[#F4F4F4] border-2 border-black brutal-shadow max-w-3xl w-full max-h-[95vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="border-b-2 border-black p-4 flex items-center justify-between">
          <div className="font-mono-civic text-xs uppercase tracking-[0.3em] text-black" style={{ fontWeight: 900 }}>
            {t("share_title")}
          </div>
          <button onClick={onClose}
            className="font-mono-civic text-xs uppercase tracking-widest px-3 py-1 border-2 border-black bg-white hover:bg-black hover:text-white"
            style={{ fontWeight: 900 }} data-testid="share-close-btn">
            {t("share_close")}
          </button>
        </div>

        <div className="p-4 border-b-2 border-black bg-white">
          <div className="w-full" style={{ aspectRatio: `${W} / ${H}` }}
            dangerouslySetInnerHTML={{ __html: svgString }} data-testid="share-preview" />
          <div className="mt-2 font-mono-civic text-[10px] uppercase tracking-widest text-black/60">
            {t("share_preview_note")}
          </div>
        </div>

        <div className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <button onClick={downloadPng} disabled={downloading}
            className="bg-black text-white uppercase tracking-widest py-3 px-3 border-2 border-black brutal-shadow-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all disabled:opacity-60 text-xs sm:text-sm"
            style={{ fontWeight: 900 }} data-testid="share-download-btn">
            {downloading ? t("share_saving") : t("share_download")}
          </button>
          <button onClick={openTwitter}
            className="bg-white text-black uppercase tracking-widest py-3 px-3 border-2 border-black hover:bg-black hover:text-white transition-colors text-xs sm:text-sm"
            style={{ fontWeight: 900 }} data-testid="share-twitter-btn">
            {t("share_twitter")}
          </button>
          <button onClick={openFacebook}
            className="bg-white text-black uppercase tracking-widest py-3 px-3 border-2 border-black hover:bg-black hover:text-white transition-colors text-xs sm:text-sm"
            style={{ fontWeight: 900 }} data-testid="share-facebook-btn">
            {t("share_facebook")}
          </button>
          <button onClick={copyText}
            className="bg-white text-black uppercase tracking-widest py-3 px-3 border-2 border-black hover:bg-black hover:text-white transition-colors text-xs sm:text-sm"
            style={{ fontWeight: 900 }} data-testid="share-copy-btn">
            {t("share_copy")}
          </button>
        </div>

        <div className="px-4 pb-4 font-mono-civic text-[10px] uppercase tracking-widest text-black/60">
          {t("share_tip")}
        </div>
      </div>
    </div>
  );
}
